import { ChangeDetectionStrategy, Component, computed, inject, signal, OnInit } from '@angular/core';
import { FormlyModule, type FormlyFieldConfig } from '@ngx-formly/core';
import { AbstractControl, ReactiveFormsModule, ValidationErrors } from '@angular/forms';
import { isEqual } from 'lodash-es';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { InfoTooltipComponent } from '../info-tooltip.component';
import { FormlyErrorComponent } from '../formly-error.component';
import { LinkButtonComponent } from '../link-button.component';
import { catchError, of, Subject, takeUntil } from 'rxjs';
import { cloneDeep } from 'lodash-es';
import { injectDestroy } from 'ngxtension/inject-destroy';
import { ApiMappingsStore } from '../../../features/admin/modules/features/api-mappings/api-mapping.store';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { FuseAlertComponent } from '../../../../@fuse/components/alert';
import {
  resolveRdwFieldMeta,
  rdwPreviewRank,
  rdwSubKey,
  rdwVisibilityKey,
  RdwFieldType,
  type RdwCategoryProfile,
  type RdwVehicleCardItem,
} from './rdw.types';
import { RDW_CATEGORY_API_FIELDS, RDW_CATEGORY_PROFILE_KEY, resolveRdwCategoryProfile } from './rdw-category.util';
import { ModulesStore } from '../../../features/admin/modules/modules.store';
import { UserStore } from '../../../core/services/user.store';
import { CascadingAutoFillService, type AutoFillResult } from '../../services/cascading-auto-fill.service';
import { UtilityService } from '../../services/utility.service';
import { DateHelperService } from '../../services/date-helper.service';
import { extractArrayIndicesFromField, setScopedKeyPath } from '../../utils/mapping-condition-evaluator.util';
import { debugLog } from '../../utils/debug-logger';
import { bareDutchPlate, formatDutchPlate } from '../../utils/dutch-license-plate.util';
import { LicensePlateComponent } from '../license-plate.component';
import { RdwVehicleCardComponent } from '../rdw-vehicle-card.component';
import { FormHelper } from '../../../features/admin/modules/features/form-builder/form.util';
import type { AutoFillMapping, Entry } from '../../../features/admin/modules/modules.model';
import { ClearFieldButtonComponent } from './clear-field-button.component';
import { buildClearAriaLabel, ClearableFieldType } from './clearable-field.type';
import { resolveRdwVisibleKeys } from './rdw-visibility.util';
import { commitUserValue, markUserEdit } from './user-edit.util';
import { EntryPatchService } from './services/entry-patch.service';

/** One configured vehicle field, with its current value pulled from the object model. */
interface RdwVehicleItem {
  /** RDW apiField this item was configured from (e.g. `merk`). */
  apiField: string;
  /** Local key inside the RDW object value (keyPath-safe form of the apiField). */
  subKey: string;
  /** Mapping-output keyPath the lookup result writes to — absent when the field is not mapped. */
  entryField?: string;
  label: string;
  type: RdwFieldType;
  value: unknown;
}

@Component({
  selector: 'formly-field-rdw',
  template: `
    <div class="flex w-full flex-col">
      <!--
        No field.className here: formly already applies it to the host <formly-field>, so
        repeating it compounded width classes (a half-width field at 50% of 50%).
      -->
      <div class="pb-4">
        @if (props.label) {
          <label class="mb-2 block font-medium" [for]="id">
            {{ props.label }}
            @if (props.required) {
              <span>*</span>
            }
            <app-info-tooltip [tooltip]="props.info"></app-info-tooltip>
          </label>
        }
        <div class="flex items-center gap-3">
          <!-- The same plate as the read view (#21239): dd-license-plate draws field, band and
               lettering; this field only projects its input and clear button into it. -->
          <dd-license-plate
            class="dd-plate dd-clear-field"
            [stretch]="true"
            [class.dd-plate--disabled]="isDisabled()">
            <input
              #plateInput
              class="dd-plate-input"
              type="text"
              autocomplete="off"
              [id]="id"
              [required]="props.required"
              [placeholder]="props.placeholder ? props.placeholder : 'AB123C'"
              [value]="plateInputValue()"
              [disabled]="isDisabled()"
              (focus)="plateFocused.set(true)"
              (blur)="plateFocused.set(false); formControl.markAsTouched()"
              (input)="onPlateInput($event)"
              (compositionend)="onPlateCompositionEnd($event)"
              (keydown.enter)="$event.preventDefault(); lookup()" />
            <app-clear-field-button
              [field]="field"
              [hasValue]="hasClearableValue()"
              [disabled]="isDisabled()"
              [ariaLabel]="clearAriaLabel()"
              [focusTarget]="plateInput"
              (clearSelection)="clear()" />
          </dd-license-plate>
          <button
            type="button"
            mat-flat-button
            class="dd-plate-lookup dd-locked-action shrink-0"
            [disabled]="isLoading() || !plate() || isDisabled()"
            (click)="lookup()">
            @if (isLoading()) {
              <mat-spinner diameter="18"></mat-spinner>
            } @else {
              Opzoeken
            }
          </button>
        </div>
        @if (invalidPlate()) {
          <div class="mt-1 text-sm text-warn">Geen geldig Nederlands kenteken.</div>
        }
        @if (props.description) {
          <div class="mt-1 text-[15px] text-accent">{{ props.description }}</div>
        }
      </div>

      @if (vehicleItems().length) {
        @if (manual()) {
          <!-- Manual entry: the configured vehicle fields become editable, prefilled with
               whatever a licence-plate lookup already returned (same object keys). Wrapped in a
               bordered card with a heading, matching the design. -->
          <div class="mb-4 rounded-lg border border-[color:var(--fuse-border)] p-4">
            <p class="mb-3 text-sm text-accent">Vul de voertuiggegevens hieronder handmatig in.</p>
            <div class="grid w-full grid-cols-1 gap-3 sm:grid-cols-2">
              @for (item of vehicleItems(); track item.subKey) {
                <mat-form-field class="w-full">
                  <mat-label>{{ item.label }}</mat-label>
                  <input
                    #manualInput
                    matInput
                    [type]="item.type"
                    [value]="item.value ?? ''"
                    [disabled]="isDisabled()"
                    (input)="setField(item.subKey, $any($event.target).value, item.type)" />
                  <span class="dd-clear-suffix" matSuffix>
                    <app-clear-field-button
                      [field]="field"
                      [hasValue]="hasManualValue(item.value)"
                      [disabled]="isDisabled()"
                      [ariaLabel]="manualFieldClearAriaLabel(item.label)"
                      [focusTarget]="manualInput"
                      (clearSelection)="clearField(item.subKey, item.type)" />
                  </span>
                </mat-form-field>
              }
            </div>
          </div>
        } @else if (readOnlyItems().length) {
          <!-- Looked-up (non-manual): the filled fields read-only, in reading order. -->
          <dd-rdw-vehicle-card [items]="readOnlyItems()" />
        }

        <div class="mb-4">
          <button
            type="button"
            dd-link-button
            class="dd-locked-action"
            [disabled]="isDisabled()"
            (click)="toggleManual()">
            {{ manual() ? 'Handmatige invoer verbergen' : 'Voertuig handmatig invoeren' }}
          </button>
        </div>
      }

      @if (lookupError(); as error) {
        <fuse-alert class="pb-6" type="error">{{ error }}</fuse-alert>
      }
      <!-- Only after the field is left or the form is submitted: typing makes the control dirty on
           the first letter, and the required error ("look the plate up") is not yet the user's. -->
      <app-formly-error [field]="field" [requireTouched]="true"></app-formly-error>
    </div>
  `,
  imports: [
    ReactiveFormsModule,
    FormlyModule,
    MatInputModule,
    MatButtonModule,
    InfoTooltipComponent,
    FormlyErrorComponent,
    LinkButtonComponent,
    MatProgressSpinner,
    FuseAlertComponent,
    ClearFieldButtonComponent,
    LicensePlateComponent,
    RdwVehicleCardComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  styles: [
    `
      /* Plate chrome (field, band, size, colors) is dd-license-plate's — shared with the read
         view. Only what is specific to EDITING a plate lives here. */
      .dd-plate-input {
        flex: 1 1 auto;
        min-width: 0;
        border: 0;
        outline: none;
        background: transparent;
        /* 2px of top padding nudges the caps down 1px: the same optical centring the printed plate
           applies to its value (caps of the mono stack sit about 1px above the box centre). */
        padding: 2px 2.25rem 0 0.75rem;
        /* Lettering comes from the plate, so an edited plate reads exactly like a read one. */
        font: inherit;
        letter-spacing: inherit;
        text-transform: uppercase;
        color: inherit;
      }

      .dd-plate-input::placeholder {
        color: rgba(0, 0, 0, 0.4);
        text-transform: none;
      }

      .dd-plate--disabled,
      .dd-plate--disabled .dd-plate-band,
      .dd-plate--disabled .dd-plate-input {
        cursor: not-allowed;
      }

      /* A disabled plate used to look exactly like an editable one (only the cursor changed).
         The plate has no form-field around it and so no field opacity: like every other control
         without one it carries the disabled label token itself, here on the lettering. */
      .dd-plate--disabled {
        // The plate is a physical object: _license-plate.scss pins its colours in .dark, so the
        // disabled lettering gets its own scheme-stable token instead of --dd-label-disabled.
        --dd-plate-fg: var(--dd-plate-fg-disabled, #a9a9ad);
      }

      :host .dd-locked-action:disabled {
        cursor: not-allowed !important;
      }

      .dd-plate app-clear-field-button {
        position: absolute;
        right: 0.5rem;
        top: 50%;
        transform: translateY(-50%);
      }

      /* Secondary filled lookup button (Figma: gray fill, no border, same height as the plate). */
      /* !important: _buttons.scss pins unelevated buttons to 3px !important globally. */
      :host ::ng-deep .dd-plate-lookup.mat-mdc-unelevated-button.mdc-button {
        --mdc-filled-button-container-height: 36px;
        --mdc-filled-button-container-shape: 8px;
        height: 36px;
        min-height: 36px;
        border-radius: 8px !important;
        background-color: #eeeff1;
        color: #000;
      }

      :host-context(.dark) ::ng-deep .dd-plate-lookup.mat-mdc-unelevated-button.mdc-button {
        background-color: rgba(255, 255, 255, 0.12);
        color: #fff;
      }

    `,
  ],
})
export class FormlyRDWType extends ClearableFieldType implements OnInit {
  destroy$ = injectDestroy();
  apiMappingsStore = inject(ApiMappingsStore);
  modulesStore = inject(ModulesStore);
  cascadingAutoFillService = inject(CascadingAutoFillService);
  readonly #entryPatchService = inject(EntryPatchService);
  readonly #userStore = inject(UserStore);
  isDutchLicensePlate = signal(false);
  isLoading = signal(false);
  invalidPlate = signal(false);
  /** Message shown when the RDW lookup request itself fails (e.g. a 500 from the mapping API). */
  lookupError = signal<string | null>(null);

  /**
   * Emits when a new lookup starts, cancelling whatever the previous one still had running.
   *
   * The sibling fill outlives its lookup: `isLoading` is already off when its query goes out, so a
   * second plate can be looked up while the first is still resolving a category. Without this the
   * slower of the two wins and writes the wrong vehicle's category.
   */
  readonly #lookupRestarted$ = new Subject<void>();

  /** The RDW field's value is an object: `{ plate, manual, <vehicle sub-fields> }`. */
  obj = signal<Record<string, unknown>>({});

  plate = computed(() => (this.obj()['plate'] as string) ?? '');

  readonly plateFocused = signal(false);

  /**
   * What the plate input shows (#21255): the sidecode grouping ("00-BPZ-9") while you are not
   * typing in it, the stored bare form while you are. Formatting on every keystroke would rewrite
   * the value under the caret and throw it to the end on any edit in the middle; the stored value
   * stays bare either way (see `setPlate`).
   */
  readonly plateInputValue = computed(() => (this.plateFocused() ? this.plate() : formatDutchPlate(this.plate())));
  manual = computed(() => this.obj()['manual'] === true);

  /** Definitions of the configured vehicle fields (RDW apiFields + their mapping target). */
  /** The API mapping configured on this field, or undefined when it is not (yet) loaded. */
  private activeMapping() {
    return this.apiMappingsStore
      .apiMappings()
      [this.options.formState.entity.id]?.find(m => m.id === this.props['apiMappingId']);
  }

  vehicleFieldDefs = computed<Array<Omit<RdwVehicleItem, 'value'>>>(() => {
    const apiFields =
      (this.props['vehicleFields'] as string[] | undefined) ??
      (this.props['displayFields'] as string[] | undefined) ??
      [];
    if (!apiFields.length) {
      return [];
    }

    const mapping = this.activeMapping();

    return apiFields.map(apiField => {
      const meta = resolveRdwFieldMeta(apiField);
      const entryField = mapping?.outputFieldMappings?.find(o => o.apiField === apiField)?.entryField ?? undefined;
      return { apiField, subKey: rdwSubKey(apiField), entryField, label: meta.label, type: meta.type };
    });
  });

  /** The configured fields with their current values (recomputes as the object model changes). */
  vehicleItems = computed<RdwVehicleItem[]>(() => {
    const model = this.obj();
    return this.vehicleFieldDefs().map(def => ({ ...def, value: model[def.subKey] }));
  });

  /**
   * Filled fields only — used for the read-only (looked-up) view, with dates shown as dd-mm-yyyy.
   * Also drops fields the owning company has hidden via its "Zichtbare voertuigvelden" setting,
   * and puts the rest in the card's reading order (`rdwPreviewRank`; a stable sort, so fields the
   * order does not name keep their configured order).
   */
  readOnlyItems = computed<Array<RdwVehicleItem & RdwVehicleCardItem>>(() => {
    const allowedKeys = this.#companyVisibleKeys();
    return this.vehicleItems()
      .filter(item => item.value !== null && item.value !== undefined && item.value !== '')
      .filter(item => {
        const visibilityKey = rdwVisibilityKey(item.apiField);
        return visibilityKey === null || allowedKeys === null || allowedKeys.includes(visibilityKey);
      })
      .sort((a, b) => rdwPreviewRank(a.apiField) - rdwPreviewRank(b.apiField))
      .map(item => ({
        ...item,
        display: String(item.type === 'date' ? DateHelperService.formatIsoDateDisplay(item.value) : item.value),
      }));
  });

  /**
   * Keys the VIEWER's company allows in the RDW preview, or null for "show every mapped field".
   * Whoever owns the vehicle, each user sees the facts their own company ticked (#21461 follow-up).
   */
  readonly #companyVisibleKeys = computed(() => resolveRdwVisibleKeys(this.#userStore.currentCompanyEntry()));

  /**
   * Required validity: a valid Dutch plate must be present, and either the vehicle was looked up
   * (RDW returned data — we do not check individual fields, since RDW omits some) or manual entry
   * is on (the vehicle fields themselves stay optional for now).
   */
  rdwRequiredValidator = (control: AbstractControl): ValidationErrors | null => {
    if (!this.props.required) {
      return null;
    }
    const value = (control.value ?? {}) as Record<string, unknown>;
    if (!this.validateDutchLicensePlate((value['plate'] as string) ?? '')) {
      return { rdwRequired: true };
    }
    if (value['manual'] === true) {
      return null;
    }
    return value['lookedUp'] === true ? null : { rdwRequired: true };
  };

  ngOnInit() {
    const initial = this.formControl.value;
    this.obj.set(this.normalize(initial));

    // Legacy entries stored the plate as a bare string. The mapping input reads
    // `<keyPath>.plate`, so a string value makes the lookup fail server-side. Write the object
    // shape back instead of migrating the data — with emitEvent, because the lookup posts the
    // form model (`formState.entry`), which only picks up the new value when the change emits.
    if (typeof initial === 'string' && initial !== '') {
      this.formControl.setValue(this.obj());
    }

    this.formControl.addValidators(this.rdwRequiredValidator);
    // The validator's own key has no shared message, so the generic error host fell back to
    // "Ongeldige waarde". Say what is actually missing, which depends on the entry mode.
    this.props.errorMessages = {
      ...(this.props.errorMessages ?? {}),
      rdwRequired: () =>
        this.manual() ? 'Voer een geldig kenteken in.' : 'Voer een geldig kenteken in en haal de voertuiggegevens op.',
    };
    this.formControl.updateValueAndValidity({ emitEvent: false });

    // Keep the local object mirror in sync with external patches (defaults, resets, etc.).
    // The lookup itself is on demand via the "Opzoeken" button.
    this.formControl.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(value => this.obj.set(this.normalize(value)));
  }

  lookup(): void {
    if (this.isLoading() || !this.plate()) {
      return;
    }
    this.runLookup(this.plate());
  }

  /**
   * Deviates: the composite holds more than the plate, and the button's accessible name only
   * promises to clear the plate. Lookup output goes with it (it derives from the cleared plate),
   * but manual mode and manually typed vehicle data survive — yanking the user out of manual
   * entry because they corrected the plate lost real input. Entry fields that a previous lookup
   * filled through the cascading auto-fill mapping are deliberately NOT reverted here: the user
   * may have edited them since, and the mapping writes to controls this field does not own.
   */
  override clear(): void {
    this.#lookupRestarted$.next();
    const current = this.obj();
    let cleared: Record<string, unknown>;
    if (current['manual'] === true) {
      cleared = { ...current, plate: '', lookedUp: false, checkPending: false };
      // The category profile is lookup output (written in runLookup, invisible in the manual
      // UI) and feeds the copyFieldsOnSelect query mapping — keeping it would let the cascade
      // keep matching the OLD vehicle's profile after its plate is gone.
      delete cleared[RDW_CATEGORY_PROFILE_KEY];
    } else {
      cleared = { plate: '', manual: false, lookedUp: false };
    }
    this.obj.set(cleared);
    commitUserValue(this.formControl, cleared);
    this.isDutchLicensePlate.set(false);
    this.invalidPlate.set(false);
    this.lookupError.set(null);
    this.isLoading.set(false);
  }

  /**
   * Input handler of the plate field. The box has always SHOWN upper case (CSS), but what a user
   * types or pastes is stored as typed, so the visible text and the stored plate drifted apart.
   * Upper-case the text in the box itself — keeping the caret, which a rewritten value would
   * otherwise throw to the end — so what is stored is what is shown.
   *
   * Not while an IME composition is running (`isComposing`): a mobile keyboard's suggestion or a
   * dead key is still editing the text in the box, and rewriting `input.value` under it replaces
   * that text and cancels the composition. The same text is normalised when the composition ends
   * (`onPlateCompositionEnd`), which is the last event of a composition and fires after its final
   * `input` event.
   */
  onPlateInput(event: Event): void {
    if ((event as InputEvent).isComposing) {
      return;
    }
    this.#normalisePlateInput(event.target as HTMLInputElement);
  }

  /** The composition is over: the text in the box is final, so normalise it like any other input. */
  onPlateCompositionEnd(event: Event): void {
    this.#normalisePlateInput(event.target as HTMLInputElement);
  }

  #normalisePlateInput(input: HTMLInputElement): void {
    const upper = input.value.replace(/[a-z]/g, letter => letter.toUpperCase());
    if (upper !== input.value) {
      const { selectionStart, selectionEnd } = input;
      input.value = upper;
      input.setSelectionRange(selectionStart, selectionEnd);
    }
    this.setPlate(upper);
  }

  setPlate(value: string): void {
    // Accept plates typed with dashes/spaces or in lower case but store the bare, upper-case form:
    // the RDW API expects the plate without separators, and matches it case-sensitively (see
    // `bareDutchPlate`). Editing also invalidates any previous lookup, including one still
    // in flight: landing afterwards it would stamp the old vehicle onto the new plate.
    // Dirty but not touched: touching would show the "required" error on the first keystroke,
    // before the user had a chance to run the lookup.
    this.#lookupRestarted$.next();
    this.isLoading.set(false);
    this.patch({ plate: bareDutchPlate(value), lookedUp: false, checkPending: false }, { touch: false });
    this.lookupError.set(null);
  }

  toggleManual(): void {
    this.patch({ manual: !this.manual() });
  }

  setField(subKey: string, raw: string, type: RdwFieldType): void {
    const value = type === 'number' ? (raw === '' ? null : Number(raw)) : raw;
    this.patch({ [subKey]: value });
  }

  clearField(subKey: string, type: RdwFieldType): void {
    this.setField(subKey, '', type);
  }

  /** Deviates: the field value is an object, so emptiness is the licence plate, not the value itself. */
  override hasClearableValue(): boolean {
    return this.plate() !== '';
  }

  hasManualValue(value: unknown): boolean {
    return value !== null && value !== undefined && value !== '';
  }

  /** Same trim + fallback semantics as the contract, but for a per-item label. */
  manualFieldClearAriaLabel(label: string): string {
    return buildClearAriaLabel(label);
  }

  /**
   * Merge a partial into the object value and commit it as a user edit (#21490). Every caller is a
   * user action (lookup, typing the plate, toggling manual entry, editing a vehicle field) or a
   * normalisation during one, so the form must turn dirty; otherwise DetailsService folds the result
   * into its saved baseline and the save button stays disabled. Hydration in ngOnInit deliberately
   * writes the control directly.
   */
  private patch(partial: Record<string, unknown>, { touch = true }: { touch?: boolean } = {}): void {
    const next = { ...this.obj(), ...partial };
    this.obj.set(next);
    if (touch) {
      commitUserValue(this.formControl, next);
    } else {
      this.formControl.markAsDirty();
      this.formControl.setValue(next);
    }
  }

  /** Coerce whatever the control holds (object, legacy plate string, or null) into the object shape. */
  private normalize(value: unknown): Record<string, unknown> {
    if (value && typeof value === 'object') {
      return value as Record<string, unknown>;
    }
    if (typeof value === 'string') {
      return { plate: value };
    }
    return {};
  }

  /**
   * The entry a lookup posts, with this field's value reduced to what the user entered (plate,
   * manual flag, and in manual mode the typed vehicle fields). The previous lookup's output must not
   * go along: the api-mapping keeps an entry value wherever the new vehicle has none (missing api
   * field, or a `required: false` row), so a stale category profile kept the old category without
   * asking. The response then replaces the whole object (root patch in runLookup).
   */
  private lookupRequestEntry(plate: string): Entry {
    const entry = this.options.formState.entry as Entry;
    const keyPath = this.props['keyPath'] as string | undefined;
    if (!keyPath) {
      debugLog('RDW', 'lookup posts the entry unreduced: no keyPath');
      return entry;
    }
    const current = this.obj();
    const own: Record<string, unknown> = { plate, manual: current['manual'] === true };
    if (own['manual']) {
      for (const def of this.vehicleFieldDefs()) {
        if (def.subKey in current) {
          own[def.subKey] = current[def.subKey];
        }
      }
    }
    const request = cloneDeep(entry);
    if (!setScopedKeyPath(request, keyPath, own, extractArrayIndicesFromField(this.field))) {
      debugLog('RDW', 'lookup posts the entry unreduced', { keyPath });
      return entry;
    }
    return request;
  }

  private runLookup(value: string): void {
    this.#lookupRestarted$.next();
    this.isLoading.set(true);
    this.invalidPlate.set(false);
    this.lookupError.set(null);

    // Defensive normalisation for values that entered the model with separators or in lower case
    // (e.g. legacy entries): the backend reads the plate from the entry, so the stored value must
    // be the bare, upper-case form.
    value = bareDutchPlate(value);
    if (value !== this.plate()) {
      this.patch({ plate: value });
    }

    this.isDutchLicensePlate.set(this.validateDutchLicensePlate(value));
    if (!this.isDutchLicensePlate() || !value) {
      this.invalidPlate.set(true);
      this.isLoading.set(false);
      return;
    }

    this.apiMappingsStore
      .applyMapping({
        mappingId: this.field.props.apiMappingId,
        moduleId: this.options.formState.entity.id,
        entry: this.lookupRequestEntry(value),
        message: false,
      })
      .pipe(
        catchError(error => {
          console.error(error);
          this.isDutchLicensePlate.set(false);
          this.isLoading.set(false);
          this.lookupError.set(
            'Voertuiggegevens ophalen mislukt. Probeer het opnieuw of voer de gegevens handmatig in.'
          );
          return of();
        }),
        takeUntil(this.#lookupRestarted$),
        takeUntil(this.destroy$)
      )
      .subscribe(entry => {
        if (!entry) {
          return;
        }
        const result = (entry?.entry as Record<string, unknown>) ?? {};

        // The mapping writes its outputs to real entry keyPaths (merk/model/brandstof as their
        // own module fields). Those fields are not owned by this component, so patch the applied
        // entry into the root form — otherwise the lookup result is discarded. Same pattern as
        // the BAG verify in address.component.ts. Computed once and reused below: a vehicleFieldDefs
        // entry resolves its own entryField the same way normaliseMappedDates resolves its outputs
        // (both via activeMapping().outputFieldMappings), so by the time the own-field loop and the
        // category lookup read from `normalised`, its date fields are already ISO. Mark the edit
        // before writing the root, so the baseline refresh never sees a pristine form. The patch
        // service also marks other affected pages dirty for DetailsService's save sweep.
        const normalised = this.normaliseMappedDates(result);
        markUserEdit(this.formControl);
        this.#entryPatchService.applyEntryPatchToRootForm(UtilityService.getFormOfRootForm(this.field), normalised);

        const patch: Record<string, unknown> = {};
        for (const def of this.vehicleFieldDefs()) {
          // Only mapped fields land in the lookup result; unmapped fields stay manual-only.
          if (def.entryField) {
            patch[def.subKey] = this.getValueAtPath(normalised, def.entryField);
          }
        }
        const categoryProfile = this.resolveCategoryProfile(normalised);
        patch[RDW_CATEGORY_PROFILE_KEY] = categoryProfile;
        // checkPending: a successful lookup is an RDW check. A backend action on the module
        // (event entryCreated/entryUpdated, condition `<keyPath>.checkPending` is true) stamps the
        // check dates once the entry is saved and resets the flag, which also stops it re-firing.
        this.patch({ ...patch, lookedUp: true, checkPending: true });
        this.isLoading.set(false);
        this.fillSiblingsFromLookup();
      });
  }

  /**
   * Find the control to write an auto-fill result into.
   *
   * The service derives a control path by stripping `entry.general.` from the keyPath, which holds
   * for a flat entry form but not for a wizard, where steps and branches nest the controls deeper.
   * Look the field up by its keyPath in the live Formly tree instead — every field carries its own
   * control — and keep the derived path as a fallback.
   *
   * A repeated target is the exception: every row's field carries the same keyPath, so the walker
   * would hand back the first row. There the indexed control path is the only one that identifies
   * the row the result belongs to.
   */
  private resolveTargetControl(result: AutoFillResult): AbstractControl | null {
    if (result.repeatIndex?.length) {
      return this.formControl.root.get(result.formControlPath);
    }

    let root: FormlyFieldConfig = this.field;
    while (root.parent) {
      root = root.parent;
    }

    // Same canonical walker the rest of the portal uses to resolve a field by keyPath, but run over
    // the live tree instead of the generated config, so the field it returns carries its control.
    return (
      FormHelper.getFieldByKeyPath(result.targetFieldKeyPath, root.fieldGroup)?.formControl ??
      this.formControl.root.get(result.formControlPath)
    );
  }

  /**
   * Fill sibling fields from the completed lookup.
   *
   * Runs the field's `copyFieldsOnSelect` query-mappings, which look an entry up in another module
   * using the current entry as context — for assets that resolves the category carrying the profile
   * this lookup produced.
   *
   * Overwriting an existing value is the auto-fill service's own decision: it asks the user to
   * confirm before replacing something that is already filled in.
   */
  private fillSiblingsFromLookup(): void {
    const mappings = this.props['copyFieldsOnSelect'] as AutoFillMapping[] | undefined;
    const currentEntry = this.options.formState?.entry as Entry | undefined;
    const moduleId = this.options.formState?.entity?.id as string | undefined;
    const generatedForm = moduleId ? this.modulesStore.generatedForms()[moduleId] : null;

    if (!mappings?.length) {
      debugLog('RDW:autofill', 'geen copyFieldsOnSelect op dit veld', { keyPath: this.props['keyPath'] });
      return;
    }

    if (!currentEntry || !generatedForm) {
      debugLog('RDW:autofill', 'geen entry of generatedForm', {
        moduleId,
        heeftEntry: !!currentEntry,
        heeftGeneratedForm: !!generatedForm,
        bekendeModules: Object.keys(this.modulesStore.generatedForms() ?? {}).length,
      });
      return;
    }

    debugLog('RDW:autofill', 'mappings uitvoeren', {
      aantal: mappings.length,
      doelen: mappings.map(m => m.targetFieldKey),
    });

    // A query-mapping resolves its dynamic filter against formState.entry, and setting the form
    // control does not write through to it. Sync this field's own value first, or the profile
    // reads as empty and the mapping skips silently. Scoped to this field's own repeat position:
    // inside a fieldArray an unscoped write would stamp this plate onto every row.
    const ownIndices = extractArrayIndicesFromField(this.field);
    const keyPath = this.props['keyPath'] as string | undefined;
    if (keyPath && !setScopedKeyPath(currentEntry, keyPath, this.obj(), ownIndices)) {
      debugLog('RDW:autofill', 'eigen waarde niet in de entry te schrijven', { keyPath, ownIndices });
      return;
    }

    this.cascadingAutoFillService
      .processQueryAutoFill(mappings, currentEntry, generatedForm, this.field)
      .pipe(takeUntil(this.#lookupRestarted$), takeUntil(this.destroy$))
      .subscribe(results => {
        debugLog(
          'RDW:autofill',
          'resultaten',
          results.length,
          results.map(r => r.targetFieldKeyPath)
        );
        for (const result of results) {
          const control = this.resolveTargetControl(result);
          if (!control) {
            debugLog('RDW:autofill', 'doelveld niet gevonden', result.targetFieldKeyPath);
          } else if (!isEqual(control.value, result.value)) {
            commitUserValue(control, result.value);
          }
          setScopedKeyPath(currentEntry, result.targetFieldKeyPath, result.value, result.repeatIndex ?? []);
        }
      });
  }

  /**
   * Derive the category profile from the lookup result.
   *
   * Read straight from the mapping result rather than from the configured vehicle fields: the
   * three RDW fields it needs (vehicle type, European category, body type) are plumbing for the
   * category match, not something a user should have to see in the vehicle-field list.
   *
   * Stored on the field's own value so a `copyFieldsOnSelect` query-mapping can match an
   * asset-category on it. Null when RDW gives no usable answer — the category then stays empty.
   */
  private resolveCategoryProfile(result: Record<string, unknown>): RdwCategoryProfile | null {
    const mapping = this.activeMapping();
    const read = (apiField: string): unknown => {
      const entryField = mapping?.outputFieldMappings?.find(o => o.apiField === apiField)?.entryField;
      return entryField ? this.getValueAtPath(result, entryField) : undefined;
    };

    return resolveRdwCategoryProfile({
      voertuigsoort: read(RDW_CATEGORY_API_FIELDS.vehicleType),
      europeseVoertuigcategorie: read(RDW_CATEGORY_API_FIELDS.europeanVehicleCategory),
      inrichting: read(RDW_CATEGORY_API_FIELDS.bodyType),
    });
  }

  validateDutchLicensePlate(value: string): boolean {
    if (!value) return false;

    const normalized = value.replace(/[^a-zA-Z0-9]/g, '').toUpperCase();

    // license plate should be length of 6, 7 or 8
    if (![6, 7, 8].includes(normalized.length)) {
      return false;
    }

    // only numbers and letters are allowed
    if (!/^[A-Z0-9]+$/.test(normalized)) {
      return false;
    }

    // should be at least one letter and one number
    if (!/[A-Z]/.test(normalized) || !/[0-9]/.test(normalized)) {
      return false;
    }

    return true;
  }

  /**
   * RDW returns dates as yyyymmdd and the backend writes mapping outputs unconverted
   * (ApiMappingService.ApplyApiMapping), so a date mapped straight to one of the module's own
   * datepicker fields arrives raw. DatePipe reads a purely numeric string as epoch milliseconds —
   * the list showed 01-01-1970 for every APK date (DISTRODOT-PORTAL-JV). Convert every
   * date-typed output of the active mapping before it reaches the root form; the field's own
   * vehicleFields are converted separately in runLookup and stay untouched here.
   */
  private normaliseMappedDates(result: Record<string, unknown>): Record<string, unknown> {
    const outputs = this.activeMapping()?.outputFieldMappings ?? [];
    const copy = structuredClone(result);
    for (const output of outputs) {
      if (!output.apiField || !output.entryField || resolveRdwFieldMeta(output.apiField).type !== 'date') {
        continue;
      }
      const raw = this.getValueAtPath(copy, output.entryField);
      if (raw === undefined || raw === null || raw === '') {
        continue;
      }
      this.setValueAtPath(copy, output.entryField, DateHelperService.toIsoDate(raw));
    }
    return copy;
  }

  /**
   * Mirror of `getValueAtPath`: strips the same optional leading `entry.` (the `result` object
   * this writes into is the entry's `entry` sub-object, not the full Entry document) before
   * delegating to the shared setter, same pattern as `onboarding-prefill.types.ts`.
   */
  private setValueAtPath(target: Record<string, unknown>, keyPath: string, value: unknown): void {
    UtilityService.setKeyValuePath(target as Entry, keyPath.replace(/^entry\./, ''), value);
  }

  /**
   * Deep-reads a dot-separated keyPath (with an optional leading `entry.`, matching the
   * `outputFieldMappings[].entryField` format) from an apply-mapping result.
   */
  private getValueAtPath(source: Record<string, unknown>, keyPath: string): unknown {
    const segments = keyPath.replace(/^entry\./, '').split('.');
    return segments.reduce<unknown>((current, segment) => {
      if (current !== null && typeof current === 'object') {
        return (current as Record<string, unknown>)[segment];
      }
      return undefined;
    }, source);
  }
}
